import { useRef, useState, useCallback } from 'react';
import { api, ApiError } from '../api/client';

interface Props {
  /** Restricts file types via the input accept attr and MIME validation */
  accept?: string;
  /** Max size in MB */
  maxSizeMB?: number;
  /** Existing uploaded file (filename) so the UI can show "Replace" state */
  existingFileName?: string;
  onUploaded: (info: { uploadId: string; fileName: string; sizeBytes: number }) => void;
  label?: string;
}

const DEFAULT_MAX_MB = 25;
const ALLOWED_EXT_RE = /\.(ya?ml|csv|txt|json|conf|cfg|xml|tsv|log)$/i;

export function FileUpload({
  accept,
  maxSizeMB = DEFAULT_MAX_MB,
  existingFileName,
  onUploaded,
  label = 'Upload file',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<string | undefined>(existingFileName);

  const handleSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setError(null);

      // Client-side validation — defence in depth (server re-validates)
      if (!ALLOWED_EXT_RE.test(file.name)) {
        setError('Unsupported file type. Allowed: .yaml .yml .csv .txt .json .conf .cfg .xml .tsv .log');
        return;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`File exceeds ${maxSizeMB} MB limit.`);
        return;
      }
      if (file.size === 0) {
        setError('File is empty.');
        return;
      }

      setProgress(0);
      try {
        const { uploadId } = await api.uploadFile(file, p => setProgress(p));
        setUploaded(file.name);
        onUploaded({ uploadId, fileName: file.name, sizeBytes: file.size });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : (err as Error).message);
      } finally {
        setProgress(null);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [maxSizeMB, onUploaded],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label className="tpl-ul" style={{ width: 'fit-content' }}>
        📤 {uploaded ? `Replace: ${uploaded}` : label}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleSelect}
          disabled={progress !== null}
        />
      </label>
      {progress !== null && (
        <div role="status" aria-live="polite" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
          Uploading… {progress}%
        </div>
      )}
      {uploaded && progress === null && !error && (
        <div style={{ fontSize: 11, color: 'var(--green-bright)' }}>
          ✓ Uploaded: <code>{uploaded}</code>
        </div>
      )}
      {error && (
        <div role="alert" style={{ fontSize: 11, color: 'var(--red)' }}>
          ✗ {error}
        </div>
      )}
    </div>
  );
}
